/**
 * SKIPCAR — Exemples d'usage MUI 5
 * Fichier : src/examples/SkipcarComponents.tsx
 *
 * Patterns de composants à copier-coller dans l'app.
 * Chaque section correspond à un composant ou pattern récurrent.
 */

import React from 'react'
import {
  Box, Button, Chip, Card, CardContent,
  TableRow, TableCell,
  Alert, LinearProgress, Typography,
} from '@mui/material'
import { tokens, statusChipProps, scoreColor, scoreChipColor, scoreLabel, type SkipcarStatus } from './theme'

// ─────────────────────────────────────────────────────────────
// PATTERN 1 — Boutons
// ─────────────────────────────────────────────────────────────
export const ButtonExamples = () => (
  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
    {/* Action principale */}
    <Button variant="contained" color="primary">
      Soumettre à Skipcar →
    </Button>

    {/* Action secondaire */}
    <Button variant="outlined" color="inherit">
      Sauvegarder
    </Button>

    {/* Danger soft */}
    <Button variant="contained" color="error">
      Redemander un document
    </Button>

    {/* Dark */}
    <Button variant="contained" color="secondary">
      Voir le dossier
    </Button>

    {/* Ghost = outlined neutre */}
    <Button variant="outlined" color="inherit" size="small">
      Annuler
    </Button>

    {/* Chargement */}
    <Button variant="contained" color="primary" disabled>
      Envoi en cours…
    </Button>
  </Box>
)

// ─────────────────────────────────────────────────────────────
// PATTERN 2 — Status Chips
// ─────────────────────────────────────────────────────────────
export const StatusChip = ({ status }: { status: SkipcarStatus }) => {
  const props = statusChipProps[status]
  return (
    <Chip
      label={props.label}
      color={props.color}
      size="small"
      // Dot simulé via ::before dans le CSS global,
      // ou via icon prop :
      // icon={<Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'currentColor', ml: '4px !important' }} />}
    />
  )
}

export const StatusChipsExample = () => (
  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
    <StatusChip status="draft" />
    <StatusChip status="pending" />
    <StatusChip status="toVerify" />
    <StatusChip status="critical" />
    <StatusChip status="blocked" />
    <StatusChip status="submitted" />
    <StatusChip status="delivered" />
    <StatusChip status="autoValidated" />
  </Box>
)

// ─────────────────────────────────────────────────────────────
// PATTERN 3 — Score bar (confidence IA)
// ─────────────────────────────────────────────────────────────
export const ScoreBar = ({ score }: { score: number }) => {
  const color = scoreColor(score)
  const chipColor = scoreChipColor(score)

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flex: 1, maxWidth: 80 }}>
        <LinearProgress
          variant="determinate"
          value={score}
          sx={{
            '& .MuiLinearProgress-bar': { backgroundColor: color },
          }}
        />
      </Box>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color, minWidth: '32px' }}
      >
        {score}%
      </Typography>
      <Chip label={scoreLabel(score)} color={chipColor} size="small" />
    </Box>
  )
}

// ─────────────────────────────────────────────────────────────
// PATTERN 4 — Stat card
// ─────────────────────────────────────────────────────────────
export const StatCard = ({
  label,
  value,
  sub,
  accent = 'yellow',
}: {
  label: string
  value: string | number
  sub?: string
  accent?: 'yellow' | 'green' | 'red' | 'orange'
}) => {
  const accentColor = {
    yellow: tokens.color.yellow,
    green:  tokens.color.green,
    red:    tokens.color.red,
    orange: tokens.color.orange,
  }[accent]

  return (
    <Card
      sx={{
        borderLeft: `3px solid ${accentColor}`,
        flex: 1,
        minWidth: 140,
      }}
    >
      <CardContent>
        <Typography variant="overline">{label}</Typography>
        <Typography
          variant="h4"
          sx={{ color: accentColor, my: '2px', lineHeight: 1 }}
        >
          {value}
        </Typography>
        {sub && (
          <Typography variant="caption">{sub}</Typography>
        )}
      </CardContent>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────
// PATTERN 5 — Queue table row
// ─────────────────────────────────────────────────────────────
export const DossierTableRow = ({
  id,
  vehicle,
  buyer,
  received,
  status,
  score,
  onOpen,
}: {
  id: string
  vehicle: { immat: string; model: string }
  buyer: { name: string; agency: string }
  received: string
  status: SkipcarStatus
  score: number
  onOpen: () => void
}) => {
  const rowBg: Record<string, string> = {
    blocked:  '#FFF9F9',
    toVerify: '#FFFDF0',
  }

  return (
    <TableRow
      sx={{
        backgroundColor: rowBg[status] || 'transparent',
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#FAFAF8' },
      }}
      onClick={onOpen}
    >
      <TableCell sx={{ fontWeight: 700 }}>{id}</TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {vehicle.immat}
        </Typography>
        <Typography variant="caption">{vehicle.model}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {buyer.name}
        </Typography>
        <Typography variant="caption">{buyer.agency}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="caption">{received}</Typography>
      </TableCell>
      <TableCell>
        <StatusChip status={status} />
      </TableCell>
      <TableCell>
        <ScoreBar score={score} />
      </TableCell>
      <TableCell align="right">
        <Button
          size="small"
          variant={score >= 85 ? 'contained' : 'outlined'}
          color={score >= 85 ? 'primary' : 'inherit'}
          onClick={(e) => { e.stopPropagation(); onOpen() }}
        >
          {score < 40 ? 'Redemander' : score >= 85 ? 'Soumettre' : 'Ouvrir'}
        </Button>
      </TableCell>
    </TableRow>
  )
}

// ─────────────────────────────────────────────────────────────
// PATTERN 6 — Alert IA tip
// ─────────────────────────────────────────────────────────────
export const AITip = ({
  children,
  severity = 'warning',
}: {
  children: React.ReactNode
  severity?: 'info' | 'success' | 'warning' | 'error'
}) => (
  <Alert severity={severity} variant="standard" sx={{ mt: 1 }}>
    {children}
  </Alert>
)

// ─────────────────────────────────────────────────────────────
// PATTERN 7 — Upload zone
// ─────────────────────────────────────────────────────────────
export const UploadZone = ({
  name,
  hint,
  done,
  extractedText,
  tip,
  tipSeverity = 'warning',
  onUpload,
}: {
  name: string
  hint: string
  done?: boolean
  extractedText?: string
  tip?: string
  tipSeverity?: 'info' | 'success' | 'warning' | 'error'
  onUpload?: () => void
}) => (
  <Box
    sx={{
      border:       `0.5px solid ${done ? tokens.color.greenBorder : tokens.color.border}`,
      borderLeft:   `3px solid ${done ? tokens.color.green : tokens.color.yellow}`,
      borderRadius: tokens.radius.lg,
      p: '13px',
      backgroundColor: tokens.color.white,
    }}
  >
    {/* Header */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '5px' }}>
      <Box
        sx={{
          width: 22, height: 22, borderRadius: '50%',
          backgroundColor: done ? tokens.color.greenLight : tokens.color.amberLight,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '10px', fontWeight: 700,
          color: done ? tokens.color.green : tokens.color.amberDeep,
          flexShrink: 0,
        }}
      >
        {done ? '✓' : '!'}
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
        {name}
      </Typography>
      <StatusChip status={done ? 'autoValidated' : 'pending'} />
    </Box>

    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
      {hint}
    </Typography>

    {/* Drop zone */}
    <Box
      onClick={!done ? onUpload : undefined}
      sx={{
        border:          `1.5px ${done ? 'solid' : 'dashed'} ${done ? tokens.color.greenBorder : tokens.color.border}`,
        borderRadius:    tokens.radius.md,
        p: '12px',
        textAlign:       'center',
        backgroundColor: done ? '#FAFFFE' : tokens.color.surface,
        cursor:          done ? 'default' : 'pointer',
        transition:      `border-color ${tokens.motion.fast}`,
        '&:hover': done ? {} : {
          borderColor: tokens.color.yellow,
          backgroundColor: tokens.color.amberLight,
        },
      }}
    >
      {done ? (
        <Typography variant="caption" sx={{ color: tokens.color.green, fontWeight: 700 }}>
          {extractedText}
        </Typography>
      ) : (
        <>
          <Typography variant="body2" sx={{ color: tokens.color.textFaint }}>
            ↑ Glisser ou cliquer pour importer
          </Typography>
          <Typography variant="caption" sx={{ color: tokens.color.textFaint }}>
            JPG · PNG · PDF · max 10 Mo
          </Typography>
        </>
      )}
    </Box>

    {/* Tip IA */}
    {tip && (
      <AITip severity={done && tipSeverity !== 'warning' ? tipSeverity : tipSeverity}>
        {tip}
      </AITip>
    )}
  </Box>
)

// ─────────────────────────────────────────────────────────────
// PATTERN 8 — Sidebar item (nav dark)
// ─────────────────────────────────────────────────────────────
export const SidebarItem = ({
  icon,
  label,
  active,
  count,
  countAlert,
  onClick,
}: {
  icon: React.ReactNode
  label: string
  active?: boolean
  count?: number
  countAlert?: boolean
  onClick?: () => void
}) => (
  <Box
    onClick={onClick}
    sx={{
      display:         'flex',
      alignItems:      'center',
      gap:             '9px',
      padding:         '7px 10px',
      margin:          '1px 6px',
      borderRadius:    tokens.radius.md,
      cursor:          'pointer',
      backgroundColor: active ? tokens.color.darkMid : 'transparent',
      // Note contraste : sur fond dark #1C1C1E, #666 ratio ~3:1 → illisible.
      // #B0B0AE ratio ~7.8:1, reste clairement secondaire vs le blanc actif.
      color:           active ? tokens.color.white : '#B0B0AE',
      transition:      `all ${tokens.motion.fast}`,
      fontSize:        tokens.font.sm,
      fontWeight:      active ? 600 : 500,
      '&:hover': {
        backgroundColor: tokens.color.darkMid,
        color:           tokens.color.white,
      },
    }}
  >
    <Box sx={{ opacity: active ? 1 : 0.75, display: 'flex', width: '16px', flexShrink: 0 }}>
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>{label}</Box>
    {count !== undefined && (
      <Box
        sx={{
          fontSize:        '9px',
          fontWeight:      700,
          backgroundColor: countAlert ? '#3D1A1A' : '#333',
          // #888 sur #333 ratio ~3.5:1. Les chiffres doivent rester lisibles.
          color:           countAlert ? tokens.color.red : '#C7C7C5',
          borderRadius:    tokens.radius.full,
          padding:         '1px 6px',
          minWidth:        '20px',
          textAlign:       'center',
        }}
      >
        {count}
      </Box>
    )}
  </Box>
)

// ─────────────────────────────────────────────────────────────
// PATTERN 9 — Confidence field row
// ─────────────────────────────────────────────────────────────
export const ConfidenceRow = ({
  field,
  value,
  confidence,
  warn,
}: {
  field: string
  value: string
  confidence: number
  warn?: boolean
}) => {
  const color = scoreColor(confidence)

  return (
    <Box
      sx={{
        display:         'flex',
        alignItems:      'center',
        padding:         '7px 14px',
        borderBottom:    `0.5px solid ${tokens.color.border}`,
        gap:             '10px',
        backgroundColor: warn ? '#FFFDF0' : 'transparent',
        '&:last-child':  { borderBottom: 'none' },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          flex:       '0 0 160px',
          color:      warn ? tokens.color.amberDark : tokens.color.textMuted,
          fontWeight: warn ? 600 : 400,
        }}
      >
        {field}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          flex:       1,
          fontWeight: warn ? 700 : 700,
          color:      warn ? tokens.color.amberDark : tokens.color.dark,
        }}
      >
        {value}
      </Typography>
      <Box sx={{ flex: '0 0 80px', height: '4px', backgroundColor: '#F0F0EE', borderRadius: '2px', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${confidence}%`, backgroundColor: color, borderRadius: '2px' }} />
      </Box>
      <Typography
        variant="caption"
        sx={{ flex: '0 0 32px', textAlign: 'right', fontWeight: 700, color }}
      >
        {confidence}%
      </Typography>
    </Box>
  )
}

// ─────────────────────────────────────────────────────────────
// PATTERN 10 — Toast / Notification
// ─────────────────────────────────────────────────────────────
export const SkipcarToast = ({
  message,
  sub,
  variant = 'default',
}: {
  message: string
  sub?: string
  variant?: 'default' | 'success' | 'warning' | 'error'
}) => {
  const iconMap = { default: '●', success: '✓', warning: '!', error: '✗' }
  const colorMap = {
    default: tokens.color.textMuted,
    success: tokens.color.green,
    warning: tokens.color.yellow,
    error:   tokens.color.red,
  }

  return (
    <Box
      sx={{
        display:         'flex',
        alignItems:      'center',
        gap:             '10px',
        backgroundColor: tokens.color.dark,
        borderRadius:    tokens.radius.lg,
        padding:         '10px 14px',
        boxShadow:       tokens.shadow.lg,
        maxWidth:        '360px',
      }}
    >
      <Box
        sx={{
          width:           18,
          height:          18,
          borderRadius:    '50%',
          backgroundColor: colorMap[variant],
          display:         'flex',
          alignItems:      'center',
          justifyContent:  'center',
          fontSize:        '9px',
          fontWeight:      800,
          color:           variant === 'warning' ? tokens.color.dark : tokens.color.white,
          flexShrink:      0,
        }}
      >
        {iconMap[variant]}
      </Box>
      <Box>
        <Typography
          variant="body2"
          sx={{ color: tokens.color.white, fontWeight: 700, lineHeight: 1.3 }}
        >
          {message}
        </Typography>
        {sub && (
          <Typography variant="caption" sx={{ color: '#C7C7C5' }}>
            {sub}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

// ─────────────────────────────────────────────────────────────
// sx() helpers — raccourcis à usage fréquent
// ─────────────────────────────────────────────────────────────

export const sxCard = {
  backgroundColor: tokens.color.white,
  border:          `0.5px solid ${tokens.color.border}`,
  borderRadius:    `${tokens.radius.xl}px`,
  boxShadow:       tokens.shadow.card,
  overflow:        'hidden',
}

export const sxCardAccent = (color: string) => ({
  ...sxCard,
  borderLeft: `3px solid ${color}`,
})

export const sxSidebar = {
  width:           '210px',
  backgroundColor: tokens.color.dark,
  display:         'flex',
  flexDirection:   'column' as const,
  height:          '100vh',
  position:        'fixed' as const,
  left:            0,
  top:             0,
  borderRight:     `1px solid ${tokens.color.darkBorder}`,
}

export const sxTopbar = {
  height:          '48px',
  borderBottom:    `0.5px solid ${tokens.color.border}`,
  display:         'flex',
  alignItems:      'center',
  padding:         '0 20px',
  gap:             '12px',
  backgroundColor: '#FAFAF9',
  flexShrink:      0,
}

export const sxTableHead = {
  '& th': {
    backgroundColor: tokens.color.surface,
    fontSize:        tokens.font.xs,
    fontWeight:      700,
    color:           tokens.color.textFaint,
    letterSpacing:   tokens.font.widest,
    textTransform:   'uppercase',
    borderBottom:    `0.5px solid ${tokens.color.border}`,
    padding:         '10px 14px',
    whiteSpace:      'nowrap',
  },
}
